import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { WEATHER_LABELS, WEATHER_COLORS, type WeatherEvent } from '../sim/weather';

/**
 * Persistent HUD badge showing the current weather: the live event when one is
 * active, the incoming event when one is announced, or "Clear" otherwise. Only
 * shown when the mode has weather enabled. Anchored by its horizontal centre
 * (container x = badge centre) so the caller can pin it to screen-centre.
 */
export class WeatherIndicator {
  readonly container = new Container();
  private bg = new Graphics();
  private label: Text;
  private lastKey = '';

  constructor() {
    this.container.label = 'weather-indicator';
    this.container.addChild(this.bg);
    this.label = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: 'Fredoka, sans-serif',
        fontSize: 13,
        fontWeight: '600',
        fill: 0xffffff,
      }),
    });
    this.label.anchor.set(0.5, 0.5);
    this.container.addChild(this.label);
    this.container.visible = false;
  }

  update(active: WeatherEvent | null, next: WeatherEvent | null, enabled: boolean): void {
    if (!enabled) {
      this.container.visible = false;
      return;
    }
    this.container.visible = true;

    let text: string;
    let color: number;
    if (active) {
      text = `\u26c8 ${WEATHER_LABELS[active.type]}`;
      color = WEATHER_COLORS[active.type];
    } else if (next) {
      text = `\u23f3 Incoming: ${WEATHER_LABELS[next.type]}`;
      color = WEATHER_COLORS[next.type];
    } else {
      text = 'Weather: Clear';
      color = 0x9fb4c8;
    }

    const key = `${text}|${color}`;
    if (key === this.lastKey) return;
    this.lastKey = key;

    this.label.text = text;
    this.label.style.fill = color;
    const h = 24;
    const w = this.label.width + 24;
    this.bg.clear();
    this.bg
      .roundRect(-w / 2, 0, w, h, 8)
      .fill({ color: 0x05101f, alpha: 0.78 })
      .stroke({ width: 1.5, color, alpha: 0.85 });
    this.label.position.set(0, h / 2);
  }
}
